import { VisitStatus } from "@app/models/enums/visit-status.enum";

/**
 * Targets the bulk visit transform endpoint accepts. Kept narrow on purpose so the picker UI
 * stays predictable; the backend rejects anything outside this set.
 */
export const BULK_TRANSFORM_TARGETS: readonly VisitStatus[] = [
  VisitStatus.CANCELLED,
  VisitStatus.REFERRED,
  VisitStatus.AUTO
] as const;

export type BulkTransformTarget =
  VisitStatus.CANCELLED | VisitStatus.REFERRED | VisitStatus.AUTO;

/**
 * Static deny matrix mirroring `visit_status_rules.json` for the bulk-supported targets only.
 * Only entries with `deny: true` (or missing rule) are listed; everything else is allowed.
 *
 * Server-side ticket/job lifecycle checks (`denyTicketStatus`, `denyJobStatuses`) are NOT
 * mirrored here — those are evaluated authoritatively by the server during partial-apply, and
 * any visits failing those checks come back in `skippedVisits`. The client-side count is
 * therefore an upper bound on eligibility, not a guarantee.
 */
const DENY_FROM_BY_TARGET: Record<
  BulkTransformTarget,
  ReadonlySet<VisitStatus>
> = {
  [VisitStatus.CANCELLED]: new Set<VisitStatus>([
    VisitStatus.IN_PROGRESS,
    VisitStatus.COMPLETED
    // CANCELLED -> CANCELLED is a no-op, handled separately as "already in target"
  ]),
  [VisitStatus.REFERRED]: new Set<VisitStatus>([
    VisitStatus.IN_PROGRESS,
    VisitStatus.CANCELLED,
    VisitStatus.COMPLETED,
    VisitStatus.AUTO
  ]),
  [VisitStatus.AUTO]: new Set<VisitStatus>([
    VisitStatus.BOOKED,
    VisitStatus.FLOATING,
    VisitStatus.IN_PROGRESS,
    VisitStatus.CANCELLED,
    VisitStatus.COMPLETED
  ])
};

export interface VisitLike {
  id: number;
  visitStatus: VisitStatus;
}

export interface EligibilitySummary {
  target: BulkTransformTarget;
  eligible: VisitLike[];
  ineligible: VisitLike[];
  alreadyInTarget: VisitLike[];
}

/**
 * Returns whether a single visit can be transformed to `target` based on the static rule matrix.
 * Visits already in the target status are NOT eligible (would be a no-op).
 */
export function isVisitEligibleForTarget(
  visit: VisitLike,
  target: BulkTransformTarget
): boolean {
  if (!visit) {
    return false;
  }
  if (visit.visitStatus === target) {
    return false;
  }
  const denied = DENY_FROM_BY_TARGET[target];
  return !denied.has(visit.visitStatus);
}

/**
 * Buckets a selection by eligibility for the given target. Used by the bulk dialog to render
 * the "X eligible / Y skipped" preview before the user submits.
 */
export function summariseEligibility(
  visits: VisitLike[],
  target: BulkTransformTarget
): EligibilitySummary {
  const summary: EligibilitySummary = {
    target,
    eligible: [],
    ineligible: [],
    alreadyInTarget: []
  };
  for (const visit of visits ?? []) {
    if (!visit) {
      continue;
    }
    if (visit.visitStatus === target) {
      summary.alreadyInTarget.push(visit);
    } else if (isVisitEligibleForTarget(visit, target)) {
      summary.eligible.push(visit);
    } else {
      summary.ineligible.push(visit);
    }
  }
  return summary;
}
